import {
  BusinessServerAndTables,
  EntitySetFromDb,
  Floorplan,
  HasFloorplan,
  ID,
  PartyAndState,
  PartyAndTable,
  PartyUpdate,
  StateUpdateWithoutParty,
  TableAndPosition,
  TableAndState,
  TextMessage,
} from 'state/service/appTs/types';
import { BusinessTag, ServiceStateAppStrict } from 'state/service/app';
import { ElmWalkInPartyDescription, PartyFromElm, TableFromElm } from 'types/service';
import { ServiceAppError } from 'state/service/appTs/serviceErrors';
import {
  addPartyToPatron,
  addTableToFloorplan,
  addTableToParty,
  addTextMessageForPhone,
  applyPartyUpdate,
  checkPartyDoesNotExist,
  displayPartyName,
  eligibleForAutoClose,
  getTable,
  getTableLayout,
  getTables,
  handleTableUpdate,
  recomputeServerPartyMapping,
  removePartyFromPatron,
  replacePartyInState,
  seatAtTableIfPossible,
  updateStateOfPartyAtTable,
  updateWithServerState,
  walkInParty,
  withTableAndParty,
} from 'state/service/appTs/helpers';
import {
  addValueToMultimap,
  insertAllToMultimap,
  removeKeyValueFromMultimap,
  removeValueFromMultimap,
} from 'state/service/appTs/utils';
import { assertNever } from '@tock/shared/utils/typeUtils';
import {
  compact,
  concat,
  filter,
  flatten,
  includes,
  isEmpty,
  map,
  mapValues,
  omit,
  sortBy,
  uniq,
} from 'es-toolkit/compat';
import { encodeFloorplan } from 'state/service/appTs/encoders';

/*
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMCore.elm#L439-L446
 */
export function addAvailableTableTag(
  app: ServiceStateAppStrict,
  key: string,
  val: string
): ServiceStateAppStrict {
  return {
    ...app,
    availableTableTags: {
      ...app.availableTableTags,
      [key]: sortBy(uniq(concat(app.availableTableTags[key] || [], val))),
    },
  };
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMCore.elm#L446-L447
 */
export function addBusinessGroupTag(
  app: ServiceStateAppStrict,
  businessTag: BusinessTag
): ServiceStateAppStrict {
  return { ...app, businessTag: { ...app.businessTag, [businessTag.id]: businessTag } };
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMCore.elm#L449-L454
 */
export function addExtraTableToParty(
  app: ServiceStateAppStrict,
  payload: PartyAndTable
): ServiceStateAppStrict {
  return withTableAndParty(app, payload.tableId, payload.partyId, addTableToParty(app));
}

export function addParty(
  model: ServiceStateAppStrict,
  party: PartyFromElm,
  tableIds: ID[]
): ServiceStateAppStrict {
  checkPartyDoesNotExist(party.id, model);

  const patronIdToPartyIds = party.patronInfo.id
    ? addPartyToPatron(party.patronInfo.id, party.id, model.patronIdToPartyIds)
    : model.patronIdToPartyIds;

  const partyIdToTableId = insertAllToMultimap(model.partyIdToTableId, party.id, tableIds);

  return {
    ...model,
    parties: {
      ...model.parties,
      [party.id]: party,
    },
    patronIdToPartyIds,
    partyIdToTableId,
  };
}

export function addTable(app: ServiceStateAppStrict, table: TableFromElm): ServiceStateAppStrict {
  const tables = getTables(getTableLayout(app));
  if (tables[table.id]) {
    throw new ServiceAppError(`Table ID ID ${table.id} exists`);
  }
  return addTableToFloorplan(app, table);
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMCore.elm#L483-L496
 */
export function addTextMessage(
  app: ServiceStateAppStrict,
  textMessage: TextMessage
): ServiceStateAppStrict {
  return {
    ...app,
    parties: mapValues(app.parties, (party) => party && addTextMessageForPhone(party, textMessage)),
  };
}

/*
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMCore.elm#L754-L760
 */
export function addTextMessageToParty(
  app: ServiceStateAppStrict,
  { partyId, textMessage }: { partyId: number; textMessage: TextMessage }
): ServiceStateAppStrict {
  const party = app.parties[partyId];
  if (!party) {
    throw new ServiceAppError(`Party ID ${partyId} does not exist`);
  }
  return {
    ...app,
    parties: {
      ...app.parties,
      [party.id]: {
        ...party,
        textMessage: sortBy([...party.textMessage, textMessage], 'id'),
      },
    },
  };
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMCore.elm#L498-L508
 */
export function addWalkInParty(
  app: ServiceStateAppStrict,
  partyDescription: ElmWalkInPartyDescription
): ServiceStateAppStrict {
  return {
    ...app,
    parties: { ...app.parties, [partyDescription.id]: walkInParty(partyDescription) },
    patronIdToPartyIds: partyDescription.patronId
      ? addValueToMultimap(app.patronIdToPartyIds, partyDescription.patronId, partyDescription.id)
      : app.patronIdToPartyIds,
  };
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMCore.elm#L514-L546
 */
export function applyEntitySetFromDb(
  app: ServiceStateAppStrict,
  entitySetFromDb: EntitySetFromDb
): ServiceStateAppStrict {
  return app; // TODO
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMCore.elm#L806-L823
 */
export function assignBusinessServerToTables(
  app: ServiceStateAppStrict,
  { businessServerId, tableId: tableIds }: BusinessServerAndTables
): ServiceStateAppStrict {
  for (const tableId of tableIds) {
    if (!app.tables[tableId]) {
      throw new ServiceAppError(`Table ID ${tableId} does not exist`);
    }
  }

  const updateServerId = (table: TableFromElm): TableFromElm => {
    if (!includes(tableIds, table.id)) {
      return table;
    }
    return {
      ...table,
      businessServerId,
    };
  };

  return recomputeServerPartyMapping({
    ...app,
    tables: mapValues(app.tables, (table) => table && updateServerId(table)),
  });
}

/**
 * Rewritten from
 * https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMCore.elm#L510-L512
 */
export function assignPartyToTable(
  app: ServiceStateAppStrict,
  { partyId, tableId }: PartyAndTable
): ServiceStateAppStrict {
  const party = app.parties[partyId];
  if (!party) {
    throw new ServiceAppError(`Party ID ${partyId} does not exist`);
  }

  const table = app.tables[tableId];
  if (!table) {
    throw new ServiceAppError(`Table ID ${tableId} does not exist`);
  }
  return addTableToParty({
    ...app,
    partyIdToTableId: omit(app.partyIdToTableId, partyId),
    serverIdToPartyIds: removeValueFromMultimap(app.serverIdToPartyIds, partyId),
  })(table, party);
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMCore.elm#L548-L564
 */
export function closeAllParties(
  app: ServiceStateAppStrict,
  timestampSecs: number
): ServiceStateAppStrict {
  return Object.values(app.parties).reduce((appUpdated, party) => {
    return party && eligibleForAutoClose(party)
      ? updatePartyState(appUpdated, {
          partyId: party.id,
          secondaryState: undefined,
          state: 'LEFT',
          timestamp: timestampSecs,
        })
      : appUpdated;
  }, app);
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMCore.elm#L779-L799
 */
export function cutServer(app: ServiceStateAppStrict, payload: { id: ID }): ServiceStateAppStrict {
  return recomputeServerPartyMapping({
    ...app,
    tables: mapValues(
      app.tables,
      (t) =>
        t && {
          ...t,
          businessServerId: t.businessServerId === payload.id ? null : t.businessServerId,
        }
    ),
  });
}

/*
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMCore.elm#L566-L570
 */
export function markTextMessagesAsRead(
  app: ServiceStateAppStrict,
  { partyId }: { partyId: number }
): ServiceStateAppStrict {
  const party = app.parties[partyId];
  if (!party) {
    throw new ServiceAppError(`Party ID ${partyId} does not exist`);
  }
  return {
    ...app,
    parties: {
      ...app.parties,
      [partyId]: {
        ...party,
        textMessage: map(party.textMessage, (msg) => {
          return {
            ...msg,
            isRead: true,
          };
        }),
      },
    },
  };
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMCore.elm#L570-L576
 */
export function moveTable(
  app: ServiceStateAppStrict,
  payload: TableAndPosition
): ServiceStateAppStrict {
  const table = app.tables[payload.tableId];
  if (!table) {
    throw new ServiceAppError(`Table ID ${payload.tableId} does not exist`);
  }
  return {
    ...app,
    tables: {
      ...app.tables,
      [table.id]: {
        ...table,
        location: table.location && { ...table.location, ...payload.newPosition },
      },
    },
  };
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMCore.elm#L581-L587
 */
export function removeAllTablesFromParty(
  app: ServiceStateAppStrict,
  partyId: ID
): ServiceStateAppStrict {
  if (!app.parties[partyId]) {
    throw new ServiceAppError(`Party ID ${partyId} does not exist`);
  }

  return {
    ...app,
    partyIdToTableId: omit(app.partyIdToTableId, partyId),
    serverIdToPartyIds: removeValueFromMultimap(app.serverIdToPartyIds, partyId),
  };
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMCore.elm#L589-L596
 */
export function removeAvailableTableTag(
  app: ServiceStateAppStrict,
  key: string,
  val: string
): ServiceStateAppStrict {
  const availableTableTags = app.availableTableTags;
  const newValuesForKey = filter(availableTableTags[key], (v) => v !== val);

  const newAvailableTableTags = {
    ...availableTableTags,
    [key]: newValuesForKey,
  };
  if (isEmpty(newValuesForKey)) {
    delete newAvailableTableTags[key];
  }

  return {
    ...app,
    availableTableTags: newAvailableTableTags,
  };
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMCore.elm#L600-L612
 */
export function removeParty(app: ServiceStateAppStrict, partyId: ID): ServiceStateAppStrict {
  const party = app.parties[partyId];
  if (!party) {
    throw new ServiceAppError(`Party with ID ${partyId} does not exist`);
  }

  return {
    ...app,
    parties: omit(app.parties, party.id),
    patronIdToPartyIds: removePartyFromPatron(app, party.id),
  };
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMCore.elm#L614-L616
 */
export function removePartyFromTable(
  app: ServiceStateAppStrict,
  payload: PartyAndTable
): ServiceStateAppStrict {
  const party = app.parties[payload.partyId];
  const table = app.tables[payload.tableId];
  if (!party || !table) {
    throw new ServiceAppError('Party or table did not exist');
  }

  const tablesForParty = app.partyIdToTableId[party.id];
  if (!tablesForParty || !tablesForParty.includes(table.id)) {
    throw new ServiceAppError(
      `The ${displayPartyName(party)} party is not assigned to table ${table.name}`
    );
  }

  return recomputeServerPartyMapping({
    ...app,
    partyIdToTableId: removeKeyValueFromMultimap(app.partyIdToTableId, party.id, table.id),
  });
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMCore.elm#L762-L777
 */
export function removeServer(
  app: ServiceStateAppStrict,
  payload: { tableIds: ID[] }
): ServiceStateAppStrict {
  return recomputeServerPartyMapping({
    ...app,
    tables: mapValues(app.tables, (t) =>
      t && payload.tableIds.includes(t.id) ? { ...t, businessServerId: null } : t
    ),
  });
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMCore.elm#L618-L624
 */
export function removeTable(app: ServiceStateAppStrict, tableId: ID): ServiceStateAppStrict {
  const table = getTable(tableId, getTableLayout(app));
  if (!table) {
    throw new ServiceAppError(`Table ID ${tableId} does not exist`);
  }
  return handleTableUpdate(app, { ...table, isArchived: true });
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMCore.elm#L631-L637
 */
export function removeTextMessageFromParty(
  app: ServiceStateAppStrict,
  partyId: ID,
  textMessage: TextMessage
): ServiceStateAppStrict {
  const currentParty = app.parties[partyId];
  if (!currentParty) {
    throw new ServiceAppError(`Party ID ${partyId} does not exist`);
  }

  return replacePartyInState(app, {
    ...currentParty,
    textMessage: sortBy(
      currentParty.textMessage.filter((tm) => tm.id !== textMessage.id),
      'id'
    ),
  });
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMCore.elm#L639-L647
 */
export function removeWalkInParty(
  app: ServiceStateAppStrict,
  payload: { partyId: ID }
): ServiceStateAppStrict {
  const party = app.parties[payload.partyId];
  if (!party) {
    throw new ServiceAppError(`Party with ID ${payload.partyId} does not exist`);
  }

  if (party.state === 'SEATED') {
    throw new ServiceAppError(`The ${displayPartyName(party)} party is seated`);
  }

  if (party.partyType.partyType !== 'WalkInParty') {
    throw new ServiceAppError(`The ${displayPartyName(party)} party is not a walk-in party`);
  }

  return {
    ...app,
    parties: omit(app.parties, party.id),
    partyIdToTableId: omit(app.partyIdToTableId, party.id),
  };
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMCore.elm#L649-L664
 */
export function replaceParty(
  app: ServiceStateAppStrict,
  party: PartyFromElm
): ServiceStateAppStrict {
  const originalParty = app.parties[party.id];
  if (!originalParty) {
    throw new ServiceAppError(`Party ID ${party.id} does not exist`);
  }

  return {
    ...app,
    parties: {
      ...app.parties,
      [party.id]: updateWithServerState(originalParty, party),
    },
    // TODO: handle update to partyIdToTableId
  };
}

/*
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMCore.elm#L666-L668
 */
export function resetTablePositions(app: ServiceStateAppStrict): ServiceStateAppStrict {
  const resetTablePosition = (table: TableFromElm | undefined): TableFromElm | undefined => {
    if (!table) {
      // istanbul ignore next
      return undefined;
    }

    if (!table.location || !table.location.defaultPosition) {
      return table;
    }

    return {
      ...table,
      location: {
        ...table.location,
        xPosition: table.location.defaultPosition.xPosition,
        yPosition: table.location.defaultPosition.yPosition,
      },
    };
  };

  return {
    ...app,
    tables: mapValues(app.tables, resetTablePosition),
  };
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMCore.elm#L669-L694
 */
export function setFloorplan(
  app: ServiceStateAppStrict,
  floorplan: Floorplan,
  partyAssignments: PartyAndTable[]
): ServiceStateAppStrict {
  const tableLayout: HasFloorplan = {
    type: 'HasFloorplan',
    floorplan: floorplan,
  };
  const partyIdToTableId = partyAssignments.reduce(
    (assignments: DictionaryStrict<ID[]>, assignment) => {
      const { partyId, tableId } = assignment;
      return partyId && tableId
        ? {
            ...assignments,
            [partyId]: [...(assignments[partyId] || []), tableId],
          }
        : assignments;
    },
    {}
  );
  const partyIds = Object.keys(partyIdToTableId);
  const mappedTableIds = flatten(compact(Object.values(partyIdToTableId)));
  const tableIdsInNewFloorplan = Object.keys(floorplan.tables).map(Number);
  for (const partyId of partyIds) {
    if (!app.parties[partyId]) {
      throw new ServiceAppError(`Party ID ${partyId} not found`);
    }
  }
  for (const tableId of mappedTableIds) {
    if (!tableIdsInNewFloorplan.includes(tableId)) {
      throw new ServiceAppError(`Table ID ${tableId} does not exist`);
    }
  }

  return recomputeServerPartyMapping({
    ...app,
    floorplan: encodeFloorplan(tableLayout.floorplan),
    tables: tableLayout.floorplan.tables, // TODO handle table layout properly
    partyIdToTableId,
  });
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMCore.elm#L696-L698
 */
export function setPartyWithIdToParty(
  app: ServiceStateAppStrict,
  party: PartyFromElm
): ServiceStateAppStrict {
  if (!app.parties[party.id]) {
    throw new ServiceAppError(`Party ID ${party.id} does not exist`);
  }
  return replacePartyInState(app, party);
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMCore.elm#L700-L706
 */
export function setTextMessagesForParty(
  app: ServiceStateAppStrict,
  partyId: ID,
  textMessages: TextMessage[]
): ServiceStateAppStrict {
  const currentParty = app.parties[partyId];
  if (!currentParty) {
    throw new ServiceAppError(`Party ID ${partyId} does not exist`);
  }

  return replacePartyInState(app, { ...currentParty, textMessage: textMessages });
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/5457f0eaaf26bdcada41cc7954fa06aff36aeddf/service/src/elm/main/TMCore.elm#L708-L714
 */
export function setTimesForPartyWithId(
  app: ServiceStateAppStrict,
  partyId: ID,
  leftTime: number,
  seatedTime: number
): ServiceStateAppStrict {
  const currentParty = app.parties[partyId];
  if (!currentParty) {
    throw new ServiceAppError(`Party ID ${partyId} does not exist`);
  }

  return replacePartyInState(app, {
    ...currentParty,
    leftTime: leftTime / 1000.0,
    seatedTime: seatedTime / 1000.0,
  });
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMCore.elm#L716-L723
 */
export function updateParty(
  app: ServiceStateAppStrict,
  partyUpdate: PartyUpdate
): ServiceStateAppStrict {
  const model = app;
  if (!model.parties[partyUpdate.partyId]) {
    throw new ServiceAppError(`Party ID ${partyUpdate.partyId} does not exist`);
  }
  return applyPartyUpdate(model, partyUpdate);
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMCore.elm#L1290-L1362
 */
export function updatePartyState(
  app: ServiceStateAppStrict,
  payload: PartyAndState & { timestamp: number }
): ServiceStateAppStrict {
  const { partyId, secondaryState, timestamp } = payload;
  const currentParty = app.parties[partyId];
  if (!currentParty) {
    throw new ServiceAppError(`Party ID ${partyId} does not exist`);
  }
  const updatedParty: PartyFromElm = { ...currentParty, state: payload.state, secondaryState };

  if (currentParty.state === 'CANCELED') {
    throw new ServiceAppError(`${displayPartyName(currentParty)} party cannot be un-cancelled`);
  } else if (currentParty.state === updatedParty.state) {
    return replacePartyInState(app, updatedParty);
  }

  switch (updatedParty.state) {
    case 'ARRIVED':
    case 'PARTIALLY_ARRIVED':
      return replacePartyInState(app, {
        ...updatedParty,
        leftTime: undefined,
        seatedTime: undefined,
        arrivalTime: updatedParty.arrivalTime || timestamp,
      });
    case 'SEATED':
    case 'PARTIALLY_SEATED':
      return seatAtTableIfPossible(app, updatedParty, timestamp);
    case 'EXPECTED':
    case 'NO_SHOW':
    case 'CANCELED':
      return replacePartyInState(app, {
        ...updatedParty,
        leftTime: undefined,
        seatedTime: undefined,
        arrivalTime: undefined,
      });
    case 'LEFT':
      return replacePartyInState(app, { ...updatedParty, leftTime: timestamp });
    // istanbul ignore next
    default:
      return assertNever(updatedParty.state);
  }
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMCore.elm#L727-L736
 */
export function updateTable(
  app: ServiceStateAppStrict,
  table: TableFromElm
): ServiceStateAppStrict {
  return handleTableUpdate(app, table);
}

export function updateTablePartyState(
  app: ServiceStateAppStrict,
  tableId: ID,
  stateUpdateWithoutParty: StateUpdateWithoutParty
): ServiceStateAppStrict {
  const table = getTable(tableId, getTableLayout(app));
  if (!table) {
    throw new ServiceAppError(`Table ID ${tableId} does not exist`);
  }

  return updateStateOfPartyAtTable(app, table, stateUpdateWithoutParty);
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMCore.elm#L738-L744
 */
export function updateTableState(
  app: ServiceStateAppStrict,
  payload: TableAndState
): ServiceStateAppStrict {
  const table = app.tables[payload.tableId];
  if (!table) {
    throw new ServiceAppError(`Table ID ${payload.tableId} does not exist`);
  }

  return handleTableUpdate(app, { ...table, state: payload.newState });
}
